import { useMemo } from 'react';

import { UFOExperience, useUFOComponentExperience } from '@atlassian/ufo';

import { NavigationExperienceId } from './constant';

export interface UseExperienceTrackerProps {
	id: keyof typeof NavigationExperienceId;
}

function useUFOExperience(
	id: string,
	config: UFOExperience['config'],
	instanceId?: string | null,
): UFOExperience {
	return useMemo(() => new UFOExperience(id, config, instanceId), [id, config, instanceId]);
}

/**
 * Hook-based experience tracker that automatically starts and aborts on unmount
 */
export function useExperienceTracker(key: keyof typeof NavigationExperienceId): UFOExperience {
	const experienceObj = NavigationExperienceId[key];
	const experience = useUFOExperience(
		experienceObj.experienceId,
		experienceObj.config,
		experienceObj.instanceId,
	);
	useUFOComponentExperience(experience); // this hook starts the experience and aborts it when the component is unmounted
	return experience;
}

/**
 * Non-hook factory function for creating experiences lazily.
 * You must manually call start(), success(), failure(), or abort().
 */
export function createExperienceTracker(key: keyof typeof NavigationExperienceId): UFOExperience {
	const experienceObj = NavigationExperienceId[key];
	return new UFOExperience(
		experienceObj.experienceId,
		experienceObj.config,
		experienceObj.instanceId,
	);
}
